import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Button,
  MenuItem,
  TextField,
  Typography,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  Box,
  Chip,
  Grid,
} from "@mui/material";
import DesktopBanner from "../assets/ClientBanner.jpg";
import MobileBanner from "../assets/ClientBanner.jpg";
import { InputAdornment } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import AdminApiModule from "../components/AdminApi/Api/api";
import ClientApiModule from "../components/ClientApi/api";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../components/config/config";
import VerifiedIcon from "@mui/icons-material/Verified";
import CopyButton from "../components/ServicesStatus/ClipbaordButton";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CategoryIcon from "@mui/icons-material/Category";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PublicIcon from "@mui/icons-material/Public";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import MapsHomeWorkOutlinedIcon from "@mui/icons-material/MapsHomeWorkOutlined";
import QuantityIcon from "@mui/icons-material/FormatListNumbered";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PaymentMethodDialog from "./PaymentMethodDialog";
import WarrantyRegistrationForm from "./WarrantyRegistrationForm";
import {
  AVAILABLE_TIME_PREFERENCES,
  CLIENT_OFFER_CONFIG,
} from "../utility/clientQrUtils";

const theme = createTheme({
  palette: {
    primary: {
      main: "#3f51b5",
    },
    secondary: {
      main: "#f50057",
    },
    background: {
      default: "#f8fafc",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    h5: {
      fontWeight: 700,
      fontSize: "1.5rem",
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: "1.1rem",
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          boxShadow:
            "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          borderRadius: "8px",
          padding: "8px 20px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          transform: "translate(14px, 16px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },
        },
      },
    },
  },
});

// Function to compress image
const compressImage = (file, quality = 0.2) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      resolve(file); // Return original file if not an image
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Calculate new dimensions while maintaining aspect ratio
        const maxWidth = 800;
        const maxHeight = 800;
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Canvas is empty"));
              return;
            }

            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });

            resolve(compressedFile);
          },
          "image/jpeg",
          quality,
        );
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };
  });
};

const PublicTicketBookingClientQr = () => {
  const { id: qrId } = useParams();
  const ONLINE_ONLY_CLIENT_QR_IDS = new Set(["INC87", "INC133"]);
  const normalizedQrId = (qrId || "").toUpperCase();
  const isOnlineOnlyClientQr = ONLINE_ONLY_CLIENT_QR_IDS.has(normalizedQrId);
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [bannerImage, setBannerImage] = useState(null);
  const [isBannerLoading, setIsBannerLoading] = useState(true);
  const [isWarrantyForm, setIsWarrantyForm] = useState(false);
  const [formData, setFormData] = useState({
    primaryService: "",
    primaryServiceId: "",
    subCategory: "",
    subCategoryName: "",
    requirement: "",
    modelName: "",
    name: "",
    phone: "",
    altPhone: "",
    houseNumber: "",
    locality: "",
    city: "",
    state: "",
    country: "INDIA",
    pincode: "",
    timePref: "WITHIN_24_HOURS",
    specificDateTime: null,
    offerCode: "",
    clientName: "",
    clientId: "",
    count_of_accessories: "1",
    categoryId: "",
    paymentWay: {
      isPayViaClient: false,
      isPayViaCustomer: false,
      ticketPicPrize: 0,
    },
  });
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [offerMessage, setOfferMessage] = useState("");
  const [allPrimaryServices, setAllPrimaryServices] = useState([]);
  const [isLoading, setIsLoading] = useState({
    primaryServices: false,
    subCategories: false,
    submitting: false,
    sendingOtp: false,
    verifyingOtp: false,
    processingPayment: false,
    checkingDiscount: false,
    compressingImages: false,
  });
  const [clientDataError, setClientDataError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [otpDialog, setOtpDialog] = useState({
    open: false,
    otp: "",
    verifyOtp: false,
  });
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketModalData, setTicketModalData] = useState(null);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [headerTitle, setHeaderTitle] = useState("Service Request Form");
  const CLIENT_HEADER_MAPPING = {
    "68653fba6a9c25e47b645c34": "Installation Request Form",
  };
  const [paymentMethodDialog, setPaymentMethodDialog] = useState({
    open: false,
    originalAmount: 0,
    discountedAmount: 0,
    paymentMethod: "",
  });

  // State for client-specific offer popup
  const [showDiscountPopup, setShowDiscountPopup] = useState(false);
  const [clientOfferConfig, setClientOfferConfig] = useState(
    CLIENT_OFFER_CONFIG.default,
  );

  // Function to handle invoice file upload with compression
  const handleInvoiceUpload = async (event) => {
    const files = Array.from(event.target.files);

    // Check if adding new files would exceed the limit of 5
    if (invoiceFiles.length + files.length > 5) {
      setSnackbar({
        open: true,
        message: "You can upload maximum 5 files only",
        severity: "error",
      });
      return;
    }

    setIsLoading((prev) => ({ ...prev, compressingImages: true }));

    try {
      const uploadedFiles = [];

      for (const file of files) {
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setSnackbar({
            open: true,
            message: `File ${file.name} is too large. Maximum size is 5MB.`,
            severity: "error",
          });
          continue;
        }

        // Check file type
        const validTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
          "application/pdf",
        ];
        if (!validTypes.includes(file.type)) {
          setSnackbar({
            open: true,
            message: `File ${file.name} is not a supported format. Please upload images or PDF.`,
            severity: "error",
          });
          continue;
        }

        uploadedFiles.push(file);
      }

      setInvoiceFiles((prev) => [...prev, ...uploadedFiles]);
    } catch (error) {
      console.error("Error processing files:", error);
      setSnackbar({
        open: true,
        message: "Error processing uploaded files",
        severity: "error",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, compressingImages: false }));
      event.target.value = "";
    }
  };

  // Function to handle invoice file removal
  const handleRemoveInvoice = (index) => {
    // Revoke object URL to prevent memory leaks
    const fileToRemove = invoiceFiles[index];
    if (fileToRemove.type.startsWith("image/")) {
      URL.revokeObjectURL(fileToRemove);
    }

    setInvoiceFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Function to format file size exactly like the image
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    if (i === 0) return bytes + " " + sizes[i];
    if (i === 1) return (bytes / Math.pow(k, i)).toFixed(2) + " " + sizes[i];

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      invoiceFiles.forEach((file) => {
        if (file.type.startsWith("image/")) {
          URL.revokeObjectURL(file);
        }
      });
    };
  }, [invoiceFiles]);

  // Function to get offer configuration based on client
  const getClientOfferConfig = (clientId, qrId) => {
    if (qrId && CLIENT_OFFER_CONFIG[qrId]) {
      return CLIENT_OFFER_CONFIG[qrId];
    }
    if (clientId && CLIENT_OFFER_CONFIG[clientId]) {
      return CLIENT_OFFER_CONFIG[clientId];
    }
    return CLIENT_OFFER_CONFIG.default;
  };

  // Function to fetch banner
  const fetchBanner = async (clientId) => {
    if (!clientId) return;

    setIsBannerLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}admin/get-common-banner?isActive=true&qr_id=${qrId}`,
      );

      if (response.data && response.data.success && response.data.data) {
        const bannerData = response.data.data;

        if (bannerData.length > 0 && bannerData[0].image) {
          setBannerImage(bannerData[0].image);
        } else {
          setBannerImage(isMobile ? MobileBanner : DesktopBanner);
        }
      } else {
        setBannerImage(isMobile ? MobileBanner : DesktopBanner);
      }
    } catch (error) {
      console.error("Error fetching banner:", error);
      setBannerImage(isMobile ? MobileBanner : DesktopBanner);
    } finally {
      setIsBannerLoading(false);
    }
  };

  useEffect(() => {
    if (
      !bannerImage ||
      bannerImage === MobileBanner ||
      bannerImage === DesktopBanner
    ) {
      setBannerImage(isMobile ? MobileBanner : DesktopBanner);
    }
  }, [isMobile, bannerImage]);

  // Add this function to handle warranty form submission
  const handleWarrantySubmit = async (file) => {
    setIsLoading((prev) => ({ ...prev, submitting: true }));

    try {
      const formDataToSend = new FormData();

      // Prepare the data as per API requirements
      formDataToSend.append("service_provided_for", formData.primaryServiceId);

      const personalDetails = {
        primary_phone: {
          country_code: "+91",
          mobile_number: formData.phone,
        },
        alternate_phone: {
          country_code: "+91",
          mobile_number: formData.altPhone || formData.phone,
        },
        name: formData.name,
      };
      formDataToSend.append(
        "personal_details",
        JSON.stringify(personalDetails),
      );

      const addressDetails = {
        longitude: 0,
        latitude: 0,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        additional_pincode: formData.pincode,
        country: formData.country,
      };
      formDataToSend.append("address_details", JSON.stringify(addressDetails));

      formDataToSend.append("client_obj_id", formData.clientId);
      formDataToSend.append("urldata", window.location.href);

      if (file) {
        formDataToSend.append("clientWarrentyFile", file);
      }

      const response = await axios.post(
        `${BASE_URL}center/public-warrenty-ticket-booking-withINC`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data?.success) {
        setSnackbar({
          open: true,
          message: "Warranty registered successfully!",
          severity: "success",
        });

        setFormData({
          // Reset only relevant fields or keep as needed
          primaryService: "",
          primaryServiceId: "",
          name: "",
          phone: "",
          altPhone: "",
          city: "",
          state: "",
          country: "INDIA",
          pincode: "",
        });
      } else {
        throw new Error(
          response.data?.message || "Warranty registration failed",
        );
      }
    } catch (error) {
      console.error("Error registering warranty:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to register warranty",
        severity: "error",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, submitting: false }));
    }
  };

  const fetchSubCategories = async (primaryServiceId, clientId) => {
    if (!primaryServiceId || primaryServiceId === "other" || !clientId) return;

    setIsLoading((prev) => ({ ...prev, subCategories: true }));

    try {
      // Call both APIs in parallel
      const [serviceAreaRes, categoryPriceRes] = await Promise.all([
        axios.get(
          `${BASE_URL}client/get-category-based-client-and-service?primary_services=${primaryServiceId}&client_id=${clientId}`,
        ),
        axios.get(
          `${BASE_URL}admin/get-category-based-service?primary_services=${primaryServiceId}`,
        ),
      ]);

      // Process service area data to get category IDs
      let categoryIds = [];
      if (
        serviceAreaRes.data &&
        serviceAreaRes.data.success &&
        serviceAreaRes.data.data &&
        serviceAreaRes.data.data.formatted &&
        serviceAreaRes.data.data.formatted.length > 0
      ) {
        const serviceData = serviceAreaRes.data.data.formatted[0];
        const categoryDetails = serviceData.serviceAreas?.categories || [];

        // Extract category IDs from the first API
        categoryIds = categoryDetails.map((category) => category.category_id);
      }

      // Process category price data from second API
      let subCategories = [];
      if (
        categoryPriceRes.data &&
        categoryPriceRes.data.success &&
        categoryPriceRes.data.data &&
        categoryPriceRes.data.data.names
      ) {
        // Filter categories that exist in both APIs
        subCategories = categoryPriceRes.data.data.names
          .filter((category) => categoryIds.includes(category._id))
          .map((category) => ({
            id: category._id,
            name: category.label || "",
            name2: category.label2 || "",
            price: category.category_service_prize || 0,
          }));
      }

      setFormData((prev) => {
        const quantity = prev.count_of_accessories || 1;

        // Auto-select the first sub-category if available
        let updatedData = {
          ...prev,
          subCategories: subCategories,
        };

        if (subCategories.length > 0) {
          const firstSubCategory = subCategories[0];
          const price = firstSubCategory.price * quantity;

          // Format: label - label2 @ ₹price
          const subCategoryDisplay = `${firstSubCategory.name}${
            firstSubCategory.name2 ? ` - ${firstSubCategory.name2}` : ""
          }${price ? ` @ ₹${price}` : ""}`;

          updatedData = {
            ...updatedData,
            subCategory: firstSubCategory.id,
            subCategoryName: subCategoryDisplay,
            requirement: `${subCategoryDisplay}${
              prev.modelName ? ` - Model: ${prev.modelName}` : ""
            }`,
          };
        }

        return updatedData;
      });

      if (subCategories.length === 0) {
        setSnackbar({
          open: true,
          message: "No sub-categories available for this service",
          severity: "warning",
        });
      }
    } catch (err) {
      console.error("Error fetching sub-categories:", err);
      setSnackbar({
        open: true,
        message: "Failed to load sub-categories",
        severity: "error",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, subCategories: false }));
    }
  };

  useEffect(() => {
    const fetchClientData = async () => {
      if (!qrId) return;

      setIsLoading((prev) => ({ ...prev, clientData: true }));
      setClientDataError(null);

      try {
        const clientRes = await axios.get(
          `${BASE_URL}client/get-client-based-on-qr?qr_id=${qrId}`,
        );

        if (
          clientRes.data &&
          clientRes.data.data &&
          clientRes.data.data.doesCenterExists
        ) {
          const clientData = clientRes.data.data.doesCenterExists;

          // Set header based on client mapping or use default
          setHeaderTitle(
            CLIENT_HEADER_MAPPING[clientData._id] || "Service Request Form",
          );

          // Set client-specific offer configuration
          const offerConfig = getClientOfferConfig(clientData._id, qrId);
          setClientOfferConfig(offerConfig);

          // Show discount popup for this client (always show on page load, no localStorage)
          setShowDiscountPopup(true);

          // Fetch banner for this client
          await fetchBanner(clientData._id);

          // Second API call to get primary services
          const servicesRes = await axios.get(
            `${BASE_URL}admin/get-all-center-services?qr_id=${qrId}`,
          );

          if (
            servicesRes.data &&
            servicesRes.data.success &&
            Array.isArray(servicesRes.data.data)
          ) {
            const services = servicesRes.data.data.map((service) => ({
              id: service._id,
              name:
                service._id === "other" ? service.name : service.service_name,
              bookingLink: service.booking_link,
              isSpecial: service._id === "other",
            }));

            setAllPrimaryServices(services);

            // Auto-select the first primary service if available
            if (services.length > 0 && !services[0].isSpecial) {
              // Set the first service as selected
              const firstService = services[0];
              setFormData((prev) => ({
                ...prev,
                clientName: clientData.client_name, // Store client name internally
                offerCode: clientData.client_name, // Pre-fill offer code with client name
                clientId: clientData._id,
                categoryId:
                  clientData.serviceAreas.categories?.[0]?.category_id || "",
                paymentWay: {
                  isPayViaClient:
                    clientData.serviceAreas.categories?.[0]?.payment_way
                      ?.isPayViaClient || false,
                  isPayViaCustomer:
                    clientData.serviceAreas.categories?.[0]?.payment_way
                      ?.isPayViaCustomer || false,
                  ticketPicPrize:
                    clientData.serviceAreas.categories?.[0]?.payment_way
                      ?.ticketPicPrize || 0,
                },
                primaryService: firstService.name,
                primaryServiceId: firstService.id,
              }));

              // Fetch sub-categories for the first service
              await fetchSubCategories(firstService.id, clientData._id);
            } else {
              setFormData((prev) => ({
                ...prev,
                clientName: clientData.client_name, // Store client name internally
                offerCode: clientData.client_name, // Pre-fill offer code with client name
                clientId: clientData._id,
                categoryId:
                  clientData.serviceAreas.categories?.[0]?.category_id || "",
                paymentWay: {
                  isPayViaClient:
                    clientData.serviceAreas.categories?.[0]?.payment_way
                      ?.isPayViaClient || false,
                  isPayViaCustomer:
                    clientData.serviceAreas.categories?.[0]?.payment_way
                      ?.isPayViaCustomer || false,
                  ticketPicPrize:
                    clientData.serviceAreas.categories?.[0]?.payment_way
                      ?.ticketPicPrize || 0,
                },
              }));
            }
          } else {
            setSnackbar({
              open: true,
              message: "No services available for this client",
              severity: "warning",
            });
          }
        } else {
          setClientDataError("Client data not found");
          setBannerImage(isMobile ? MobileBanner : DesktopBanner);
          setIsBannerLoading(false);
        }
      } catch (err) {
        console.error("Error fetching client data:", err);
        setClientDataError("Failed to load client information");
        setBannerImage(isMobile ? MobileBanner : DesktopBanner);
        setIsBannerLoading(false);
      } finally {
        setIsLoading((prev) => ({ ...prev, clientData: false }));
      }
    };

    fetchClientData();
  }, [qrId, isMobile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePrimaryServiceChange = async (e) => {
    const primaryServiceId = e.target.value;
    const selectedService = allPrimaryServices.find(
      (service) => service.id === primaryServiceId,
    );

    if (selectedService?.isSpecial) {
      if (selectedService.bookingLink) {
        window.location.href = "https://www.onit.services/service-form";
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      primaryService: selectedService?.name || "",
      primaryServiceId: primaryServiceId,
      subCategory: "",
      subCategoryName: "",
      requirement: "",
      modelName: "",
      subCategories: [],
    }));

    if (primaryServiceId && primaryServiceId !== "other" && formData.clientId) {
      await fetchSubCategories(primaryServiceId, formData.clientId);
    }
  };

  const handleSubCategoryChange = (e) => {
    const subCategoryId = e.target.value;
    const selectedSubCategory = formData.subCategories?.find(
      (subCat) => subCat.id === subCategoryId,
    );

    const quantity = parseInt(formData.count_of_accessories) || 1;
    const price = selectedSubCategory
      ? selectedSubCategory.price * quantity
      : 0;

    const subCategoryDisplay = selectedSubCategory
      ? `${selectedSubCategory.name}${
          selectedSubCategory.name2 ? ` - ${selectedSubCategory.name2}` : ""
        }${price ? ` @ ₹${price}` : ""}`
      : "";

    setFormData((prev) => ({
      ...prev,
      subCategory: subCategoryId,
      subCategoryName: subCategoryDisplay,
      requirement: `${subCategoryDisplay}${
        prev.modelName ? ` - Model: ${prev.modelName}` : ""
      }${quantity >= 1 ? ` Qty:${quantity}` : ""}`,
    }));
  };

  const handlePincodeFetch = () => {
    if (!navigator.geolocation) {
      setSnackbar({
        open: true,
        message: "Geolocation is not supported by your browser",
        severity: "error",
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        const nominatimApiUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;

        fetch(nominatimApiUrl)
          .then((response) => response.json())
          .then((data) => {
            if (data && data.address) {
              const address = data.address;
              const pincode = address.postcode || "";
              const city =
                address.city || address.town || address.village || "";
              const state = address.state || "";
              const locality =
                address.suburb || address.neighbourhood || address.hamlet || "";

              setFormData((prev) => ({
                ...prev,
                pincode,
                city,
                state,
                locality,
              }));
            } else {
              setSnackbar({
                open: true,
                message: "Pincode not found for your location",
                severity: "warning",
              });
            }
          })
          .catch((error) => {
            console.error("Error fetching location data:", error);
            setSnackbar({
              open: true,
              message: "Failed to fetch location data",
              severity: "error",
            });
          });
      },
      (error) => {
        console.error("Error getting location:", error.message);
        setSnackbar({
          open: true,
          message:
            "Unable to retrieve your location. Please check permissions.",
          severity: "error",
        });
      },
    );
  };

  const validateForm = () => {
    if (
      !formData.primaryServiceId ||
      !formData.subCategory ||
      !formData.name ||
      !formData.phone ||
      !formData.houseNumber ||
      !formData.locality ||
      !formData.city ||
      !formData.state ||
      !formData.pincode ||
      !formData.timePref
    ) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error",
      });
      return false;
    }

    if (formData.phone.length !== 10) {
      setSnackbar({
        open: true,
        message: "Please enter a valid 10-digit phone number",
        severity: "error",
      });
      return false;
    }

    if (
      formData.timePref === "SPECIFIC_DATE_AND_TIME" &&
      !formData.specificDateTime
    ) {
      setSnackbar({
        open: true,
        message: "Please select a date and time",
        severity: "error",
      });
      return false;
    }

    return true;
  };

  const handleSendOtp = async () => {
    if (!validateForm()) return;

    setIsLoading((prev) => ({ ...prev, sendingOtp: true }));

    try {
      const _data = {
        country_code: "+91",
        mobile_number: formData.phone,
      };

      await axios.post(`${BASE_URL}consumerAppAppRoute/sent-otp-by-web`, _data);

      setOtpDialog({
        open: true,
        otp: "",
        verifyOtp: true,
      });
    } catch (error) {
      console.error("Error sending OTP:", error);
      setSnackbar({
        open: true,
        message: "Failed to send OTP. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, sendingOtp: false }));
    }
  };

  useEffect(() => {
    let interval;
    if (otpDialog.open && !canResendOtp) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResendOtp(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpDialog.open, canResendOtp]);

  const handleVerifyOtp = async () => {
    if (!otpDialog.otp || otpDialog.otp.length !== 4) {
      setSnackbar({
        open: true,
        message: "Please enter a valid 4-digit OTP",
        severity: "error",
      });
      return;
    }

    setIsLoading((prev) => ({ ...prev, verifyingOtp: true }));

    try {
      const _data = {
        country_code: "+91",
        mobile_number: formData.phone,
        otp: parseInt(otpDialog.otp),
      };

      await axios.post(
        `${BASE_URL}consumerAppAppRoute/verify-otp-by-web`,
        _data,
      );

      const selectedSubCategory = formData.subCategories?.find(
        (subCat) => subCat.id === formData.subCategory,
      );

      const quantity = formData.count_of_accessories || 1;
      const originalAmount = selectedSubCategory
        ? selectedSubCategory.price * quantity
        : 0;

      if (originalAmount === 0) {
        await handleCreateTicket(false);
        setOtpDialog({
          open: false,
          otp: "",
          verifyOtp: false,
        });
      } else {
        await handleCheckUserExist();
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setSnackbar({
        open: true,
        message: "Invalid OTP. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, verifyingOtp: false }));
    }
  };

  const handleCheckUserExist = async () => {
    try {
      const res = await axios.post(
        `${BASE_URL}consumerAppAppRoute/register-consumer-by-web`,
        {
          personal_details: {
            phone: {
              country_code: "+91",
              mobile_number: formData.phone,
            },
            name: formData.name,
          },
          address_details_permanent: {
            pincode: formData.pincode,
          },
        },
      );

      if (res?.data?.status === 200) {
        if (formData.paymentWay.isPayViaCustomer) {
          const selectedSubCategory = formData.subCategories?.find(
            (subCat) => subCat.id === formData.subCategory,
          );

          const quantity = formData.count_of_accessories || 1;
          const originalAmount = selectedSubCategory
            ? selectedSubCategory.price * quantity
            : 0;

          if (originalAmount > 0) {
            setPaymentMethodDialog({
              open: true,
              originalAmount: originalAmount,
              discountedAmount: originalAmount,
              paymentMethod: "ONLINE",
            });

            // Check for discounted amount
            setIsLoading((prev) => ({ ...prev, checkingDiscount: true }));
            try {
              const discountRes = await axios.post(
                `${BASE_URL}admin/check-discounted-amount`,
                {
                  clientId: formData.clientId,
                  amount: originalAmount,
                },
              );

              if (
                discountRes.data?.success &&
                discountRes.data?.data?.finalAmount
              ) {
                setPaymentMethodDialog((prev) => ({
                  ...prev,
                  discountedAmount: discountRes.data.data.finalAmount,
                }));
              }
            } catch (error) {
              console.error("Error checking discount:", error);
            } finally {
              setIsLoading((prev) => ({ ...prev, checkingDiscount: false }));
            }

            setOtpDialog({
              open: false,
              otp: "",
              verifyOtp: false,
            });
            return;
          }
        }

        await handleCreateTicket(false);
      } else {
        throw new Error("Failed to register/check user");
      }
    } catch (error) {
      console.error("Error checking user existence:", error);
      setSnackbar({
        open: true,
        message: "Failed to verify user. Please try again.",
        severity: "error",
      });
    }
  };

  // Function to reset form data after successful submission
  const resetFormData = () => {
    setFormData((prev) => ({
      ...prev,
      // Reset personal details
      name: "",
      phone: "",
      altPhone: "",

      // Reset address details
      houseNumber: "",
      locality: "",
      city: "",
      state: "",
      pincode: "",

      // Reset other fields but keep service selection
      requirement: "",
      modelName: "",
      timePref: "WITHIN_24_HOURS",
      specificDateTime: null,
      count_of_accessories: "1",
    }));
  };

  const handleCreateTicket = async (discountApply) => {
    setIsLoading((prev) => ({ ...prev, submitting: true }));

    try {
      let requirementParts = [formData.subCategoryName];

      if (formData.modelName) {
        requirementParts.push(`${formData.modelName}`);
      }

      if (formData.count_of_accessories >= 1) {
        requirementParts.push(`Qty: ${formData.count_of_accessories}`);
      }

      const fullRequirement = requirementParts.join(" ");

      let timePreference = {
        time_preference_type: formData.timePref,
      };

      if (
        formData.timePref === "SPECIFIC_DATE_AND_TIME" &&
        formData.specificDateTime
      ) {
        const date = new Date(formData.specificDateTime);
        const isoString = date.toISOString();
        const timestamp = date.getTime();

        timePreference = {
          ...timePreference,
          specific_date_time: isoString,
        };
      }

      const selectedSubCategory = formData.subCategories?.find(
        (subCat) => subCat.id === formData.subCategory,
      );

      // Calculate amount based on quantity
      const quantity = formData.count_of_accessories || 1;
      const originalAmount = selectedSubCategory
        ? selectedSubCategory.price * quantity
        : 0;

      // Use discounted amount if applicable
      const amountToUse = discountApply
        ? paymentMethodDialog.discountedAmount
        : originalAmount;

      // Create FormData for multipart/form-data
      const formDataToSend = new FormData();

      // Add all the fields to FormData
      formDataToSend.append("service_provided_for", formData.primaryServiceId);
      formDataToSend.append("service_provided_type", formData.subCategory);
      formDataToSend.append("specific_requirement", fullRequirement);

      formDataToSend.append(
        "personal_details",
        JSON.stringify({
          primary_phone: {
            country_code: "+91",
            mobile_number: formData.phone,
          },
          alternate_phone: {
            country_code: "+91",
            mobile_number: formData.altPhone || formData.phone,
          },
          name: formData.name,
        }),
      );

      formDataToSend.append("time_preference", JSON.stringify(timePreference));
      formDataToSend.append(
        "offers_applied",
        JSON.stringify({
          offer_code: formData.offerCode || formData.clientName || "",
        }),
      );

      formDataToSend.append(
        "address_details",
        JSON.stringify({
          longitude: 0,
          latitude: 0,
          house_number: formData.houseNumber,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          additional_pincode: formData.pincode,
          country: formData.country,
          locality: formData.locality,
        }),
      );

      formDataToSend.append("client_obj_id", formData.clientId);
      formDataToSend.append(
        "count_of_accessories",
        formData.count_of_accessories || 1,
      );
      formDataToSend.append("urldata", window.location.href);
      formDataToSend.append("discountApply", discountApply ? "true" : "false");
      formDataToSend.append(
        "amountDetails",
        JSON.stringify({
          originalAmount,
          discountedAmount: discountApply
            ? paymentMethodDialog.discountedAmount
            : originalAmount,
          quantity,
          unitPrice: selectedSubCategory?.price || 0,
        }),
      );

      // Add invoice files as array - append all files with the same field name
      // This will create client_invoice_pictures: ["file1", "file2", "file3"]
      invoiceFiles.forEach((file) => {
        formDataToSend.append("client_invoice_pictures", file);
      });

      const response = await axios.post(
        `${BASE_URL}center/public-ticket-booking-withINC`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data?.success) {
        const ticketId = response.data.data?._id;
        const ticketReferenceId = response.data.data?.ticket_id;

        const amountToUse = discountApply
          ? paymentMethodDialog.discountedAmount
          : originalAmount;

        if (amountToUse > 0 && paymentMethodDialog.paymentMethod === "ONLINE") {
          setPaymentData({
            ticketId: ticketId,
            ticketReferenceId: ticketReferenceId,
            amount: amountToUse,
            phone: formData.phone,
            name: formData.name,
          });

          // Pass the correct ticket ID to the payment function
          initiateRazorpayPayment(ticketId, amountToUse);
        } else {
          setTicketModalData({
            ticket_id: ticketReferenceId,
            ticket_status: response.data.data?.ticket_status || "Created",
            createdAt: new Date().toLocaleString(),
            _id: ticketId,
          });
          setTicketModalOpen(true);
          setPaymentMethodDialog((prev) => ({ ...prev, open: false }));

          // Reset form data and clear invoice files after successful submission
          resetFormData();
          setInvoiceFiles([]);
        }
      } else {
        throw new Error(response.data?.message || "Ticket creation failed");
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          error.message ||
          "Something went wrong",
        severity: "error",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, submitting: false }));
    }
  };

  const initiateRazorpayPayment = async (ticketId, amountToCharge) => {
    try {
      const keyResponse = await axios.get(`${BASE_URL}center/get-razorpay-key`);
      const razorpayKey = keyResponse.data?.data?.key_id;

      if (!razorpayKey) throw new Error("Failed to get Razorpay key");

      const options = {
        key: razorpayKey,
        amount: amountToCharge * 100,
        currency: "INR",
        name: "OnIt Services",
        description: `Service Payment (Discounted: ₹${amountToCharge})`,
        handler: async (response) => {
          if (response.razorpay_payment_id) {
            await updateTicketAfterPayment(
              ticketId,
              amountToCharge,
              "success",
              response,
            );
            // Reset form data and clear invoice files after successful payment
            resetFormData();
            setInvoiceFiles([]);
          }
        },
        prefill: {
          name: formData.name,
          contact: formData.phone,
        },
        theme: { color: "#3399cc" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Payment error:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Payment initialization failed",
        severity: "error",
      });
    }
  };

  const updateTicketAfterPayment = async (
    ticketId,
    amount,
    status,
    razorpayResponse = null,
  ) => {
    try {
      if (!ticketId) {
        throw new Error("Ticket ID is required for payment update");
      }

      const payload = {
        ticket_obj_id: ticketId,
        payment_status: status,
        payment_amount: amount,
      };

      // Add Razorpay payment details if available
      if (razorpayResponse && razorpayResponse.razorpay_payment_id) {
        payload.razorpay_payment_id = razorpayResponse.razorpay_payment_id;
        payload.razorpay_order_id = razorpayResponse.razorpay_order_id;
        payload.razorpay_signature = razorpayResponse.razorpay_signature;
      }

      console.log("Sending payment update payload:", payload);

      const response = await axios.post(
        `${BASE_URL}center/update-ticket-after-payByWeb-client`,
        payload,
      );

      console.log("Payment update response:", response.data);

      if (response.data?.success) {
        setTicketModalData({
          ticket_id: response.data.data?.ticket_id || "N/A",
          ticket_status: response.data.data?.ticket_status || "Paid",
          createdAt: new Date().toLocaleString(),
          _id: ticketId,
        });
        setTicketModalOpen(true);
        setPaymentMethodDialog((prev) => ({ ...prev, open: false }));
      } else {
        throw new Error(response.data?.message || "Payment update failed");
      }
    } catch (error) {
      console.error("Error updating ticket after payment:", error);
      setSnackbar({
        open: true,
        message:
          "Payment was successful but ticket update failed. Please contact support.",
        severity: "error",
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSendOtp();
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleCloseTicketModal = () => {
    setTicketModalOpen(false);
  };

  const handlePaymentMethodChange = (event) => {
    setPaymentMethodDialog((prev) => ({
      ...prev,
      paymentMethod: event.target.value,
    }));
  };

  const handlePaymentMethodSubmit = async () => {
    const shouldUseCod =
      !isOnlineOnlyClientQr && paymentMethodDialog.paymentMethod === "COD";

    if (shouldUseCod) {
      // For COD, don't apply discount
      await handleCreateTicket(false);
    } else {
      setIsLoading((prev) => ({ ...prev, processingPayment: true }));

      try {
        // First create the ticket with discountApply: true for online payments
        await handleCreateTicket(true);
      } catch (error) {
        console.error("Payment process error:", error);
        setSnackbar({
          open: true,
          message: "Failed to process payment. Please try again.",
          severity: "error",
        });
      } finally {
        setIsLoading((prev) => ({ ...prev, processingPayment: false }));
      }
    }
  };

  const handlePaymentError = (error) => {
    console.error("Payment error:", error);
    setSnackbar({
      open: true,
      message:
        error.response?.data?.message ||
        "Payment failed. Please try again or contact support.",
      severity: "error",
    });

    // Optionally: Update ticket status to failed
    if (error.response?.data?.ticketId) {
      updateTicketAfterPayment(error.response.data.ticketId, 0, "failed");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Client-specific Discount Popup Modal */}
      <Modal
        open={showDiscountPopup}
        onClose={() => setShowDiscountPopup(false)}
        aria-labelledby="discount-modal-title"
        aria-describedby="discount-modal-description"
      >
        <div
          className="
      absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
      w-[90%] sm:w-[80%] md:w-[60%] lg:w-[40%]
      bg-white rounded-2xl shadow-xl 
      p-4 sm:p-6 md:p-8 
      max-h-[90vh] overflow-auto
    "
          style={{
            backgroundColor: clientOfferConfig.colors.background,
          }}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <Typography
              variant="h6"
              className="font-bold text-lg sm:text-xl md:text-2xl"
              style={{ color: clientOfferConfig.colors.primary }}
            >
              {clientOfferConfig.title}
            </Typography>
            <IconButton onClick={() => setShowDiscountPopup(false)}>
              <CloseIcon />
            </IconButton>
          </div>

          {/* Content */}
          <div className="text-center">
            <div
              className="p-4 sm:p-5 rounded-lg mb-4"
              style={{
                backgroundColor:
                  clientOfferConfig.colors.background === "#fff"
                    ? "#f8f9fa"
                    : "rgba(255,255,255,0.1)",
              }}
            >
              <Typography
                variant="h5"
                className="font-extrabold mb-2 text-2xl sm:text-3xl"
                style={{ color: clientOfferConfig.colors.primary }}
              >
                {clientOfferConfig.header}
              </Typography>
              <Typography
                variant="body1"
                className="mb-2 font-medium text-base sm:text-lg"
                style={{ color: clientOfferConfig.colors.text }}
              >
                {clientOfferConfig.description}
              </Typography>
              <Typography
                variant="body2"
                className="text-sm sm:text-base"
                style={{ color: clientOfferConfig.colors.text }}
              >
                {clientOfferConfig.subDescription}
              </Typography>
            </div>

            {/* CTA Button */}
            <div className="mt-6">
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => setShowDiscountPopup(false)}
                sx={{
                  fontWeight: "bold",
                  fontSize: "1rem",
                  py: { xs: 1.4, sm: 1.6 },
                  background: clientOfferConfig.gradient,
                  boxShadow: `0 4px 10px ${clientOfferConfig.colors.secondary}40`,
                  borderRadius: "12px",
                  textTransform: "none",
                  color: "#fff",
                }}
              >
                {clientOfferConfig.buttonText}
              </Button>
            </div>

            {/* Small urgency note */}
            <Typography
              variant="caption"
              className="block mt-3 text-xs sm:text-sm"
              style={{ color: clientOfferConfig.colors.text }}
            >
              {clientOfferConfig.urgencyText}
            </Typography>
          </div>
        </div>
      </Modal>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className="min-h-screen bg-gray-200 py-2">
          <Container maxWidth="xl" className="space-y-6">
            {/* Banner */}
            <div className="rounded-xl overflow-hidden shadow-md">
              {isBannerLoading ? (
                <div className="w-full h-32 md:h-48 bg-gray-300 animate-pulse flex items-center justify-center">
                  <CircularProgress size={24} />
                </div>
              ) : (
                <img
                  src={bannerImage}
                  alt="Service Banner"
                  className="w-full h-auto object-cover"
                  onError={(e) => {
                    console.error("Failed to load banner image:", bannerImage);
                    e.target.src = isMobile ? MobileBanner : DesktopBanner;
                  }}
                />
              )}
            </div>

            {/* Conditional rendering based on form type */}
            <Card className="shadow-lg p-2">
              <CardHeader
                title={isWarrantyForm ? "Warranty Registration" : headerTitle}
                titleTypographyProps={{
                  variant: "h5",
                  color: "primary",
                }}
                subheader={
                  isWarrantyForm
                    ? "Fill the form to register your warranty"
                    : "Fill the form to book your request"
                }
                className="border-b border-gray-200 pb-4"
                action={
                  <Button
                    variant={isWarrantyForm ? "outlined" : "contained"}
                    onClick={() => setIsWarrantyForm(!isWarrantyForm)}
                    color="primary"
                    size="small"
                    sx={{ ml: 2 }}
                  >
                    {isWarrantyForm
                      ? "Service Booking"
                      : "Warranty Registration"}
                  </Button>
                }
              />

              <CardContent>
                {isWarrantyForm ? (
                  <WarrantyRegistrationForm
                    formData={formData}
                    setFormData={setFormData}
                    handleChange={handleChange}
                    isLoading={isLoading}
                    clientId={formData.clientId}
                    onSubmit={handleWarrantySubmit}
                  />
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Service Details Section */}
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <div className="bg-blue-100 p-2 rounded-lg mr-3">
                          <CategoryIcon className="text-blue-600" />
                        </div>
                        <Typography
                          variant="subtitle1"
                          className="text-gray-600"
                        >
                          Service Details
                        </Typography>
                      </div>
                      <Divider className="my-2" />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Primary Service */}
                        <FormControl fullWidth>
                          <InputLabel id="primary-service-label">
                            Service Required
                          </InputLabel>
                          <Select
                            labelId="primary-service-label"
                            name="primaryServiceId"
                            value={formData.primaryServiceId}
                            onChange={handlePrimaryServiceChange}
                            label="Service Required"
                            disabled={isLoading.primaryServices}
                            startAdornment={
                              <InputAdornment position="start" className="ml-2">
                                <CategoryIcon className="text-gray-400" />
                              </InputAdornment>
                            }
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 300,
                                },
                              },
                            }}
                          >
                            {isLoading.primaryServices ? (
                              <MenuItem value="">
                                <div className="flex justify-center w-full">
                                  <CircularProgress size={24} />
                                </div>
                              </MenuItem>
                            ) : (
                              allPrimaryServices.map((service) => (
                                <MenuItem
                                  key={service.id}
                                  value={service.id}
                                  className={
                                    service.isSpecial
                                      ? "bg-blue-50 text-blue-600 font-semibold"
                                      : ""
                                  }
                                >
                                  {service.name}
                                  {service.isSpecial &&
                                    " (Click to book other services)"}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>

                        {/* Sub-category */}
                        {formData.primaryServiceId &&
                          !allPrimaryServices.find(
                            (s) => s.id === formData.primaryServiceId,
                          )?.isSpecial && (
                            <FormControl fullWidth>
                              <InputLabel id="sub-category-label">
                                Service Type
                              </InputLabel>
                              <Select
                                labelId="sub-category-label"
                                name="subCategory"
                                value={formData.subCategory}
                                onChange={handleSubCategoryChange}
                                label="Service Type"
                                disabled={
                                  isLoading.subCategories ||
                                  !formData.primaryServiceId
                                }
                                startAdornment={
                                  <InputAdornment
                                    position="start"
                                    className="ml-2"
                                  >
                                    <CategoryIcon className="text-gray-400" />
                                  </InputAdornment>
                                }
                              >
                                {isLoading.subCategories ? (
                                  <MenuItem value="">
                                    <div className="flex justify-center w-full">
                                      <CircularProgress size={24} />
                                    </div>
                                  </MenuItem>
                                ) : (
                                  formData.subCategories?.map((subCat) => (
                                    <MenuItem key={subCat.id} value={subCat.id}>
                                      {`${subCat.name}${
                                        subCat.name2 ? ` - ${subCat.name2}` : ""
                                      }${
                                        subCat.price
                                          ? ` @ ₹${subCat.price}`
                                          : "@ ₹0"
                                      }`}
                                    </MenuItem>
                                  ))
                                )}
                              </Select>
                            </FormControl>
                          )}

                        {/* Model Name */}
                        <TextField
                          name="modelName"
                          label="Model/Requirement"
                          fullWidth
                          value={formData.modelName}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              modelName: e.target.value,
                              requirement: `${prev.subCategoryName}${
                                e.target.value ? ` | ${e.target.value}` : ""
                              }`,
                            }));
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <ModelTrainingIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {/* Quantity */}
                        <TextField
                          name="count_of_accessories"
                          label="Quantity"
                          type="number"
                          fullWidth
                          value={formData.count_of_accessories}
                          onChange={(e) => {
                            const quantity = Math.max(
                              1,
                              parseInt(e.target.value),
                            );
                            setFormData((prev) => ({
                              ...prev,
                              count_of_accessories: quantity,
                            }));
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <QuantityIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                            inputProps: { min: 1 },
                          }}
                        />
                      </div>
                    </div>

                    {/* Enhanced Invoice Upload Section */}
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <div className="bg-teal-100 p-2 rounded-lg mr-3">
                          <AttachFileIcon className="text-teal-600" />
                        </div>
                        <Typography
                          variant="subtitle1"
                          className="text-gray-600"
                        >
                          Invoice Upload / Installation Report
                        </Typography>
                      </div>
                      <Divider className="my-2" />

                      <div className="space-y-4">
                        {/* File Upload Button */}
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                          disabled={
                            invoiceFiles.length >= 5 ||
                            isLoading.compressingImages
                          }
                          sx={{
                            borderStyle: "dashed",
                            borderWidth: "2px",
                            borderColor:
                              invoiceFiles.length >= 5
                                ? "grey.400"
                                : "primary.main",
                            py: 2,
                            width: "100%",
                            backgroundColor:
                              invoiceFiles.length >= 5
                                ? "grey.50"
                                : "transparent",
                            "&:hover": {
                              backgroundColor:
                                invoiceFiles.length >= 5
                                  ? "grey.50"
                                  : "action.hover",
                              borderColor:
                                invoiceFiles.length >= 5
                                  ? "grey.400"
                                  : "primary.dark",
                            },
                          }}
                        >
                          {isLoading.compressingImages ? (
                            <CircularProgress size={24} />
                          ) : invoiceFiles.length >= 5 ? (
                            "Maximum files reached"
                          ) : (
                            "Upload Invoice"
                          )}
                          <input
                            type="file"
                            hidden
                            multiple
                            accept=".jpg,.jpeg,.png,.gif,.pdf,image/*"
                            onChange={handleInvoiceUpload}
                          />
                        </Button>

                        {/* File Limit Info */}
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className="text-center"
                        >
                          Maximum 5 files allowed (Images or PDF, max 5MB each)
                          {invoiceFiles.length > 0 &&
                            ` - ${invoiceFiles.length}/5 files selected`}
                        </Typography>

                        {/* Uploaded Files Preview - Exactly like the image */}
                        {invoiceFiles.length > 0 && (
                          <div className="space-y-3">
                            <Typography
                              variant="subtitle2"
                              className="font-medium text-gray-700"
                            >
                              Uploaded Files:
                            </Typography>

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                              {invoiceFiles.map((file, index) => (
                                <div
                                  key={index}
                                  className="relative group bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-all duration-200"
                                >
                                  {/* Remove Button - Positioned exactly like in the image */}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveInvoice(index)}
                                    sx={{
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                      backgroundColor: "white",
                                      border: "1px solid",
                                      borderColor: "grey.300",
                                      width: 24,
                                      height: 24,
                                      opacity: 0.9,
                                      "&:hover": {
                                        backgroundColor: "grey.50",
                                        opacity: 1,
                                      },
                                    }}
                                  >
                                    <CloseIcon
                                      sx={{ fontSize: 14, color: "grey.700" }}
                                    />
                                  </IconButton>

                                  {/* File Preview Container */}
                                  <div className="flex flex-col">
                                    {file.type.startsWith("image/") ? (
                                      // Image Preview - Square aspect ratio
                                      <div className="w-full aspect-square overflow-hidden bg-gray-100">
                                        <img
                                          src={URL.createObjectURL(file)}
                                          alt={`Preview ${index + 1}`}
                                          className="w-full h-full object-cover"
                                          onLoad={() =>
                                            URL.revokeObjectURL(file)
                                          }
                                        />
                                      </div>
                                    ) : (
                                      // PDF Preview - Same square aspect ratio
                                      <div className="w-full aspect-square flex flex-col items-center justify-center bg-red-50 border-b border-red-200">
                                        <PictureAsPdfIcon
                                          sx={{
                                            fontSize: 32,
                                            color: "red.500",
                                            mb: 1,
                                          }}
                                        />
                                        <Typography
                                          variant="caption"
                                          className="text-red-600 font-medium"
                                        >
                                          PDF
                                        </Typography>
                                      </div>
                                    )}

                                    {/* File Info - Below the image */}
                                    <div className="p-2">
                                      <Typography
                                        variant="caption"
                                        className="font-medium text-gray-900 block truncate"
                                        title={file.name}
                                      >
                                        {file.name}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        className="block"
                                      >
                                        {formatFileSize(file.size)}
                                      </Typography>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Scheduling Section */}
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <div className="bg-purple-100 p-2 rounded-lg mr-3">
                          <ScheduleIcon className="text-purple-600" />
                        </div>
                        <Typography
                          variant="subtitle1"
                          className="text-gray-600"
                        >
                          Scheduling
                        </Typography>
                      </div>
                      <Divider className="my-2" />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Time Preference */}
                        <FormControl fullWidth>
                          <InputLabel id="time-pref-label">
                            Preferred Service Time
                          </InputLabel>
                          <Select
                            labelId="time-pref-label"
                            name="timePref"
                            value={formData.timePref}
                            onChange={handleChange}
                            label="Preferred Service Time"
                            startAdornment={
                              <InputAdornment position="start" className="ml-2">
                                <ScheduleIcon className="text-gray-400" />
                              </InputAdornment>
                            }
                          >
                            {AVAILABLE_TIME_PREFERENCES.map((pref) => (
                              <MenuItem key={pref.value} value={pref.value}>
                                {pref.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Date and Time Picker */}
                        {formData.timePref === "SPECIFIC_DATE_AND_TIME" && (
                          <DateTimePicker
                            label="Select Date & Time"
                            value={formData.specificDateTime}
                            onChange={(newValue) => {
                              setFormData((prev) => ({
                                ...prev,
                                specificDateTime: newValue,
                              }));
                            }}
                            minDateTime={new Date()}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                InputProps={{
                                  ...params.InputProps,
                                  readOnly: true,
                                  startAdornment: (
                                    <InputAdornment
                                      position="start"
                                      className="ml-2"
                                    >
                                      <ScheduleIcon className="text-gray-400" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}
                      </div>
                    </div>

                    {/* Personal Details Section */}
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <div className="bg-green-100 p-2 rounded-lg mr-3">
                          <PersonIcon className="text-green-600" />
                        </div>
                        <Typography
                          variant="subtitle1"
                          className="text-gray-600"
                        >
                          Personal Details
                        </Typography>
                      </div>
                      <Divider className="my-2" />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Name */}
                        <TextField
                          name="name"
                          label="Full Name"
                          fullWidth
                          value={formData.name}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <PersonIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {/* Phone Number */}
                        <TextField
                          name="phone"
                          label="Mobile Number"
                          type="tel"
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <PhoneIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                          }}
                          value={formData.phone}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            const digitsOnly = rawValue.replace(/\D/g, "");
                            if (digitsOnly.length <= 10) {
                              setFormData((prev) => ({
                                ...prev,
                                phone: digitsOnly,
                              }));
                            }
                          }}
                          inputProps={{
                            maxLength: 10,
                            inputMode: "numeric",
                          }}
                        />

                        {/* Alternate Number */}
                        <TextField
                          name="altPhone"
                          label="Alternate Number (Optional)"
                          type="tel"
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <PhoneIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                          }}
                          value={formData.altPhone}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            const digitsOnly = rawValue.replace(/\D/g, "");
                            if (digitsOnly.length <= 10) {
                              setFormData((prev) => ({
                                ...prev,
                                altPhone: digitsOnly,
                              }));
                            }
                          }}
                          inputProps={{
                            maxLength: 10,
                            inputMode: "numeric",
                          }}
                        />
                      </div>
                    </div>

                    {/* Address Section */}
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <div className="bg-orange-100 p-2 rounded-lg mr-3">
                          <LocationOnIcon className="text-orange-600" />
                        </div>
                        <Typography
                          variant="subtitle1"
                          className="text-gray-600"
                        >
                          Address Details
                        </Typography>
                      </div>
                      <Divider className="my-2" />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* House Number */}
                        <TextField
                          name="houseNumber"
                          label="House/Flat Number"
                          fullWidth
                          value={formData.houseNumber}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <HomeIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {/* Pincode */}
                        <div className="flex items-center gap-2">
                          <TextField
                            name="pincode"
                            label="Pincode"
                            fullWidth
                            value={formData.pincode}
                            onChange={handleChange}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment
                                  position="start"
                                  className="ml-2"
                                >
                                  <LocationOnIcon className="text-gray-400" />
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{
                              maxLength: 6,
                              minLength: 6,
                              inputMode: "numeric",
                            }}
                          />
                          <Tooltip title="Fetch pincode using your current location">
                            <Button
                              variant="outlined"
                              onClick={handlePincodeFetch}
                              className="h-14 min-w-[100px]"
                            >
                              Fetch
                            </Button>
                          </Tooltip>
                        </div>

                        {/* Locality */}
                        <TextField
                          name="locality"
                          label="Locality/Area"
                          fullWidth
                          value={formData.locality}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <MapsHomeWorkOutlinedIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {/* City */}
                        <TextField
                          name="city"
                          label="City"
                          fullWidth
                          value={formData.city}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <LocationCityIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {/* State */}
                        <TextField
                          name="state"
                          label="State"
                          fullWidth
                          value={formData.state}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <LocationOnIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {/* Country */}
                        <TextField
                          name="country"
                          label="Country"
                          fullWidth
                          disabled
                          value={formData.country}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <PublicIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {/* Offer Code (replaces Client Name) */}
                        <TextField
                          name="offerCode"
                          label="Offer Code"
                          fullWidth
                          value={formData.offerCode}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" className="ml-2">
                                <ConfirmationNumberIcon className="text-gray-400" />
                              </InputAdornment>
                            ),
                          }}
                          helperText="Enter your offer code if any"
                          className="col-span-1 md:col-span-2"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isLoading.sendingOtp || isLoading.submitting}
                        className="py-3 text-lg"
                      >
                        {isLoading.sendingOtp || isLoading.submitting ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Submit Request"
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* OTP Verification Dialog */}
            <Dialog
              open={otpDialog.open}
              onClose={() => setOtpDialog({ ...otpDialog, open: false })}
              PaperProps={{
                className: "rounded-2xl p-4 max-w-md",
              }}
            >
              <DialogTitle className="flex justify-between items-center pb-2">
                <Typography variant="h6" fontWeight="bold">
                  Verify OTP
                </Typography>
                <IconButton
                  onClick={() => setOtpDialog({ ...otpDialog, open: false })}
                  className="absolute right-4 top-4"
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent className="py-2">
                <Typography variant="body1" className="mb-4">
                  We've sent an OTP to your mobile number{" "}
                  <strong>+91 {formData.phone}</strong>
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  label="Enter 4-digit OTP"
                  value={otpDialog.otp}
                  onChange={(e) =>
                    setOtpDialog({ ...otpDialog, otp: e.target.value })
                  }
                  inputProps={{
                    maxLength: 4,
                    inputMode: "numeric",
                  }}
                  className="mt-6"
                />
              </DialogContent>
              <DialogActions className="justify-between px-6 pb-4">
                <Button
                  onClick={() => {
                    if (canResendOtp) {
                      setResendTimer(60);
                      setCanResendOtp(false);
                      handleSendOtp();
                    }
                  }}
                  color="primary"
                  disabled={!canResendOtp}
                  className="min-w-[120px]"
                >
                  Resend OTP {!canResendOtp && `(${resendTimer}s)`}
                </Button>
                <Button
                  onClick={handleVerifyOtp}
                  color="primary"
                  variant="contained"
                  disabled={isLoading.verifyingOtp}
                  className="min-w-[120px]"
                >
                  {isLoading.verifyingOtp ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Verify & Submit"
                  )}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Payment Method Dialog */}
            <PaymentMethodDialog
              open={paymentMethodDialog.open}
              onClose={() =>
                setPaymentMethodDialog((prev) => ({ ...prev, open: false }))
              }
              onlineOnly={isOnlineOnlyClientQr}
              originalAmount={paymentMethodDialog.originalAmount}
              discountedAmount={paymentMethodDialog.discountedAmount}
              paymentMethod={paymentMethodDialog.paymentMethod}
              onPaymentMethodChange={handlePaymentMethodChange}
              onSubmit={handlePaymentMethodSubmit}
              isLoading={isLoading.processingPayment}
              quantity={formData.count_of_accessories || 1}
            />

            {/* Success Modal for Ticket Creation */}
            <Modal
              keepMounted
              open={ticketModalOpen}
              onClose={handleCloseTicketModal}
              aria-labelledby="keep-mounted-modal-title"
              aria-describedby="keep-mounted-modal-description"
            >
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 max-h-[90vh] overflow-auto">
                <div className="flex flex-col items-center text-center mb-6">
                  <Avatar className="bg-green-100 w-12 h-12 mb-3">
                    <VerifiedIcon className="text-green-600 text-2xl" />
                  </Avatar>
                  <Typography variant="h5" className="font-bold">
                    Request Confirmed!
                  </Typography>
                  <Typography className="mt-2">
                    Thanks for choosing{" "}
                    <span className="font-bold">OniT Services</span>, an
                    initiative of ADA Enterprises! 🙏
                    <br />
                    Our certified technician will contact you soon 🤝
                  </Typography>
                </div>

                <List className="w-full mt-4 space-y-2">
                  <ListItem className="flex-col sm:flex-row items-start">
                    <ListItemText
                      primary="Request ID:"
                      className="font-medium"
                    />
                    <Typography className="break-words text-center sm:text-left">
                      {ticketModalData?.ticket_id}
                    </Typography>
                  </ListItem>
                  <Divider className="my-2" />
                  <ListItem className="flex-col sm:flex-row items-start">
                    <ListItemText primary="Status:" className="font-medium" />
                    <Typography className="text-center sm:text-left">
                      {ticketModalData?.ticket_status}
                    </Typography>
                  </ListItem>
                  <Divider className="my-2" />
                  <ListItem className="flex-col sm:flex-row items-start">
                    <ListItemText
                      primary="Created At:"
                      className="font-medium"
                    />
                    <Typography className="text-center sm:text-left">
                      {ticketModalData?.createdAt}
                    </Typography>
                  </ListItem>
                  <Divider className="my-2" />
                  <ListItem className="flex-col sm:flex-row items-start">
                    <ListItemText
                      primary="Track Your Request:"
                      className="font-medium"
                    />
                    <div className="flex flex-col sm:flex-row items-center gap-2 mt-2 sm:mt-0">
                      <CopyButton
                        webLink={`https://app.onit.services/#/ServiceStatus/?${ticketModalData?._id}`}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        href={`https://app.onit.services/#/ServiceStatus/?${ticketModalData?._id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-0 sm:ml-2"
                      >
                        Open Tracking
                      </Button>
                    </div>
                  </ListItem>
                </List>

                <div className="flex justify-center mt-6">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCloseTicketModal}
                    className="px-6 py-2"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </Modal>

            {/* Snackbar for notifications */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                className="w-full"
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Container>
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default PublicTicketBookingClientQr;
